import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  createSortableColumn,
  DataTable,
  Input,
  type TanstackReactTable,
  useDebounceCallback,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDashed,
  LayoutList,
  XCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatKpiValueByUnit } from "@/modules/performance-tracking/presentation/lib/formatKpiValue";
import { labelKpiTargetUnit } from "@/modules/training-standard/presentation/lib/kpiTargetUnitLao";
import {
  labelKpiMeasurementPeriod,
  labelKpiResultStatus,
} from "@/modules/training-standard/presentation/lib/trainingLaoLabels";
import { formatDateLocal } from "@/shared/lib/date-time";
import type { PerformanceKpiItemDTO } from "../api/client";
import {
  usePerformanceMeKpiPeriodGroupsQuery,
  usePerformanceMeKpiQuery,
} from "../api/queries";
import { performanceRoutes } from "../performanceRoutes";

/** ຈຳນວນແຖວຕໍ່ໜ້າ — ລາຍການ KPI */
const KPI_LIST_PAGE_SIZE = 10;
/** ຈຳນວນກຸ່ມ KPI ຕໍ່ໜ້າ — ຜົນຕາມຮອບ */
const PERIOD_GROUPS_PAGE_SIZE = 5;

type KpiRow = PerformanceKpiItemDTO;

function labelKpiScope(s: KpiRow["kpiScope"]): string {
  return s === "department" ? "ຜະແນກ" : "ສ່ວນຕົວ";
}

function KpiResultStatusBadge({ status }: { status: string | null }) {
  const label = labelKpiResultStatus(status);
  if (status === "pass") {
    return (
      <Badge
        variant="secondary"
        className="border-transparent bg-emerald-600/15 text-emerald-900 dark:text-emerald-100"
      >
        {label}
      </Badge>
    );
  }
  if (status === "fail") {
    return <Badge variant="destructive">{label}</Badge>;
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      {label}
    </Badge>
  );
}

export function PerformanceKpiPage() {
  const navigate = useNavigate({ from: "/app/performance/kpi" });
  const routeSearch = useSearch({ from: "/app/performance/kpi" });
  const sortBy = routeSearch.sortBy ?? "name";
  const sortOrder: "asc" | "desc" =
    routeSearch.sortOrder === "desc" ? "desc" : "asc";
  const [filterInput, setFilterInput] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const [kpiListOffset, setKpiListOffset] = useState(0);
  const [periodGroupsOffset, setPeriodGroupsOffset] = useState(0);

  const kpiListLimit = Math.max(KPI_LIST_PAGE_SIZE, 1);

  const debounceSearch = useDebounceCallback((val: string) => {
    setSearchQ(val);
    setKpiListOffset(0);
  }, 300);

  const { data, isLoading } = usePerformanceMeKpiQuery({
    sortBy,
    sortOrder,
    limit: kpiListLimit,
    offset: kpiListOffset,
    q: searchQ,
  });
  const rows = data?.data ?? [];
  const meta = data?.meta;

  const { data: periodData, isLoading: isLoadingPeriodGroups } =
    usePerformanceMeKpiPeriodGroupsQuery({
      limit: PERIOD_GROUPS_PAGE_SIZE,
      offset: periodGroupsOffset,
    });
  const periodGrouped = periodData?.data ?? [];
  const periodTotal = periodData?.meta.total ?? 0;

  useEffect(() => {
    const total = meta?.total ?? 0;
    if (kpiListOffset > 0 && kpiListOffset >= total) {
      setKpiListOffset(Math.max(0, total - kpiListLimit));
    }
  }, [meta?.total, kpiListLimit, kpiListOffset]);

  useEffect(() => {
    if (
      periodGroupsOffset > 0 &&
      periodTotal > 0 &&
      periodGroupsOffset >= periodTotal
    ) {
      setPeriodGroupsOffset(Math.max(0, periodTotal - PERIOD_GROUPS_PAGE_SIZE));
    }
  }, [periodGroupsOffset, periodTotal]);

  const columns: TanstackReactTable.ColumnDef<KpiRow>[] = [
    createSortableColumn<KpiRow>("name", "KPI", {
      size: 220,
      cell: ({ row }) => (
        <span className="font-medium">{row.original.name}</span>
      ),
    }),
    createSortableColumn<KpiRow>("kpiScope", "ຂອບເຂດ", {
      size: 120,
      cell: ({ row }) => (
        <Badge variant="secondary">
          {labelKpiScope(row.original.kpiScope)}
        </Badge>
      ),
    }),
    createSortableColumn<KpiRow>("targetValue", "ເປົ້າໝາຍ", {
      size: 140,
      cell: ({ row }) => (
        <span>
          {formatKpiValueByUnit(
            row.original.targetValue,
            row.original.targetUnit,
          )}
          {row.original.targetUnit && row.original.targetUnit !== "none"
            ? ` ${labelKpiTargetUnit(row.original.targetUnit) || row.original.targetUnit}`
            : ""}
        </span>
      ),
    }),
    createSortableColumn<KpiRow>("currentValue", "ຄ່າຈິງຫຼ້າສຸດ", {
      size: 120,
      cell: ({ row }) =>
        formatKpiValueByUnit(
          row.original.currentValue,
          row.original.targetUnit,
        ),
    }),
    createSortableColumn<KpiRow>("measurementPeriod", "ຮອບ", {
      size: 120,
      cell: ({ row }) =>
        labelKpiMeasurementPeriod(row.original.measurementPeriod),
    }),
    createSortableColumn<KpiRow>("currentStatus", "ສະຖານະ", {
      size: 130,
      cell: ({ row }) => (
        <KpiResultStatusBadge status={row.original.currentStatus} />
      ),
    }),
    {
      id: "detail",
      header: "ລາຍລະອຽດ",
      enableSorting: false,
      size: 110,
      cell: ({ row }) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            navigate({
              to: "/app/performance/kpi/$assignmentId",
              params: { assignmentId: row.original.assignmentId },
            });
          }}
        >
          ເບິ່ງ
        </Button>
      ),
    } satisfies TanstackReactTable.ColumnDef<KpiRow>,
  ];

  const kpiStats = meta
    ? {
        total: meta.total,
        pass: meta.passCount,
        fail: meta.failCount,
        pending: meta.pendingCount,
      }
    : { total: 0, pass: 0, fail: 0, pending: 0 };

  const periodGroupPageCount = Math.max(
    1,
    Math.ceil(periodTotal / PERIOD_GROUPS_PAGE_SIZE),
  );
  const periodGroupPageIndex =
    Math.floor(periodGroupsOffset / PERIOD_GROUPS_PAGE_SIZE) + 1;

  return (
    <>
      <Header />
      <Main>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="font-bold text-2xl tracking-tight">KPI ສ່ວນຕົວ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              KPI ສ່ວນຕົວ (ພະນັກງານ/ຕຳແໜ່ງ) ແລະ KPI ຜະແນກ — ຄ່າຕາມຮອບຈາກລະບົບ KPI
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0"
            onClick={() => navigate({ to: performanceRoutes.exitToAppHome })}
          >
            <ArrowLeft className="size-4" />
            ກັບໄປໜ້າຫຼັກ
          </Button>
        </div>

        <div className="mt-4">
          <div className="relative w-full max-w-md">
            <Input
              value={filterInput}
              onChange={(e) => {
                const v = e.target.value;
                setFilterInput(v);
                debounceSearch(v);
              }}
              placeholder="ຄົ້ນຫາ KPI, ຂອບເຂດ, ຮອບ ຫຼື ສະຖານະ..."
              className="pl-3"
            />
          </div>
        </div>

        {!isLoading && meta && meta.total > 0 ? (
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Card className="border-border/80 p-0 shadow-sm">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <LayoutList className="size-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">
                    ລວມ (ຕາມການກອງ)
                  </p>
                  <p className="font-semibold text-2xl tabular-nums leading-none">
                    {kpiStats.total}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/80 p-0 shadow-sm">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-emerald-600/15">
                  <CheckCircle2 className="size-5 text-emerald-700 dark:text-emerald-400" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">ຜ່ານ</p>
                  <p className="font-semibold text-2xl text-emerald-800 tabular-nums leading-none dark:text-emerald-200">
                    {kpiStats.pass}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/80 p-0 shadow-sm">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-destructive/15">
                  <XCircle className="size-5 text-destructive" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">ບໍ່ຜ່ານ</p>
                  <p className="font-semibold text-2xl tabular-nums leading-none">
                    {kpiStats.fail}
                  </p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/80 p-0 shadow-sm">
              <CardContent className="flex items-center gap-3 p-4">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/15">
                  <CircleDashed className="size-5 text-amber-800 dark:text-amber-300" />
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">ລໍຖ້າ</p>
                  <p className="font-semibold text-2xl tabular-nums leading-none">
                    {kpiStats.pending}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        ) : null}

        <Card className="mt-6 gap-0 border-border/80 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">ລາຍການ KPI</CardTitle>
            <CardDescription>
              ເລືອກສະຖານະຫຼ້າສຸດຕໍ່ຮອບ — ກົດ &quot;ເບິ່ງ&quot; ເພື່ອລາຍລະອຽດ ແລະ
              ປະຫວັດຕາມຮອບ
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <DataTable<KpiRow, unknown>
              columns={columns}
              data={rows}
              isLoading={isLoading}
              sortBy={sortBy}
              sortOrder={sortOrder}
              enablePagination
              offset={kpiListOffset}
              limit={kpiListLimit}
              totalCount={meta?.total ?? 0}
              noDataMessage={
                searchQ.trim() ? "ບໍ່ພົບ KPI ທີ່ກົງກັບການຄົ້ນຫາ" : "ບໍ່ມີ KPI ມອບໝາຍໃຫ້ທ່ານ"
              }
              onPaginationChange={(p) => {
                setKpiListOffset(p.offset);
              }}
              onSortingChange={(sorting) => {
                if (!sorting?.length) return;
                if (sorting[0]?.id === "") return;
                const first = sorting[0];
                if (!first?.id) return;
                setKpiListOffset(0);
                navigate({
                  to: "/app/performance/kpi",
                  search: {
                    sortBy: String(first.id),
                    sortOrder: first.desc ? "desc" : "asc",
                  },
                });
              }}
            />
          </CardContent>
        </Card>

        {periodTotal > 0 ? (
          <section className="mt-10 space-y-4">
            <div>
              <h2 className="font-semibold text-lg">ຜົນຕາມຮອບ</h2>
              <p className="mt-1 text-muted-foreground text-sm">
                ຈັດຕາມແຕ່ລະ KPI — ຮອບຫຼ້າສຸດຢູ່ເທິງສຸດໃນກຸ່ມ
              </p>
            </div>
            <div className="space-y-4">
              {periodGrouped.map((g) => (
                <Card
                  key={`${g.kpiName}-${g.scopeLabel}`}
                  className="gap-0 overflow-hidden border-border/80 p-0 shadow-sm"
                >
                  <CardHeader className="border-b bg-muted/30 py-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className="text-base">{g.kpiName}</CardTitle>
                      <Badge variant="secondary">{g.scopeLabel}</Badge>
                      <span className="text-muted-foreground text-xs">
                        {g.rows.length} ຮອບ
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/20 text-left">
                            <th className="p-3 font-medium">ຮອບ</th>
                            <th className="p-3 font-medium">ຄ່າຈິງ</th>
                            <th className="p-3 font-medium">ເປົ້າ (ບັນທຶກ)</th>
                            <th className="p-3 font-medium">ສະຖານະ</th>
                            <th className="p-3 font-medium">ວັດແທກ</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.rows.map((r, i) => (
                            <tr
                              key={`${g.kpiName}-${r.periodLabel}-${i}`}
                              className="border-border/60 border-b last:border-0"
                            >
                              <td className="p-3 tabular-nums">
                                {r.periodLabel}
                              </td>
                              <td className="p-3 tabular-nums">
                                {formatKpiValueByUnit(
                                  r.actualValue,
                                  g.targetUnit,
                                )}
                              </td>
                              <td className="p-3 tabular-nums">
                                {formatKpiValueByUnit(
                                  r.targetValue,
                                  g.targetUnit,
                                )}
                              </td>
                              <td className="p-3">
                                <KpiResultStatusBadge status={r.status} />
                              </td>
                              <td className="p-3 text-muted-foreground">
                                {formatDateLocal(r.measuredAt)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {periodTotal > PERIOD_GROUPS_PAGE_SIZE ? (
              <div className="flex flex-col items-center gap-3 border-t pt-4 sm:flex-row sm:justify-center">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPeriodGroupsOffset((o) =>
                        Math.max(0, o - PERIOD_GROUPS_PAGE_SIZE),
                      )
                    }
                    disabled={periodGroupsOffset === 0}
                    aria-label="ໜ້າກ່ອນ"
                  >
                    <ChevronLeft className="size-4" />
                  </Button>
                  <span className="min-w-[10rem] text-center text-muted-foreground text-sm">
                    ໜ້າ {periodGroupPageIndex} / {periodGroupPageCount} (
                    {periodTotal} ກຸ່ມ)
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPeriodGroupsOffset((o) =>
                        Math.min(
                          o + PERIOD_GROUPS_PAGE_SIZE,
                          Math.max(0, periodTotal - PERIOD_GROUPS_PAGE_SIZE),
                        ),
                      )
                    }
                    disabled={
                      periodGroupsOffset + PERIOD_GROUPS_PAGE_SIZE >=
                      periodTotal
                    }
                    aria-label="ໜ້າຖັດ"
                  >
                    <ChevronRight className="size-4" />
                  </Button>
                </div>
              </div>
            ) : null}
          </section>
        ) : !isLoadingPeriodGroups && !isLoading && meta && meta.total > 0 ? (
          <p className="mt-8 text-center text-muted-foreground text-sm">
            ຍັງບໍ່ມີລາຍການຜົນຕາມຮອບໃນລະບົບ — ຈະສະແດງເມື່ອມີການບັນທຶກຜົນ KPI
          </p>
        ) : null}
      </Main>
    </>
  );
}
